import { z } from 'zod';
import { AGE_BAND_OPTIONS, ANAESTHETIC_PRIORITY_OPTIONS, ANAESTHETIC_SETTING_OPTIONS, CASE_TYPE_OPTIONS, careLevelLabel, COMMUNICATION_CATEGORY_OPTIONS, COMMUNICATION_OUTCOME_OPTIONS, COMMUNICATION_PARTICIPANT_OPTIONS, EDT_ACTIVITY_OPTIONS, LEARNER_GRADE_OPTIONS, REFERRAL_REASON_OPTIONS, SIDE_OPTIONS, formatDuration, labelFor, } from './enums.js';
import { hilloByNumber } from './hillos.js';
import { procedureLabel } from './procedures.js';
import { anaestheticCaseRecordShape, caseRecordShape, communicationRecordShape, edtEntryRecordShape, procedureRecordShape, refineProcedure, } from './schemas.js';
import { isCase, isProcedure } from './types.js';
/**
 * The export format — the control for hazard H1, a trainee losing their
 * logbook.
 *
 * The point of this file is that it outlives the app. If ICULogbook disappears
 * tomorrow, a trainee's export must still be readable and complete enough to
 * reconstruct their record somewhere else, which is why the format is plain
 * JSON, documented in `docs/EXPORT_FORMAT.md`, and versioned.
 *
 * Two consequences worth stating:
 *
 * - **Soft-deleted entries are included.** An export is a copy of the database,
 *   not a report. Something a trainee deleted by accident three months ago is
 *   recoverable from an export, and it cannot be if the export quietly drops it.
 * - **Nothing is summarised.** Counts and percentages belong in the PDF; this is
 *   the raw record.
 */
/**
 * Bump only for a change that an older reader could not cope with. Adding an
 * optional field is not one — a reader that ignores unknown fields keeps
 * working, and every reader here does.
 *
 * Version 2 added `edtEntries`, version 3 `anaestheticCases`, version 4
 * `communications`. All *are* bumps, even though all are additive, and the
 * reason is the project's second rule rather than the format's. An older
 * build reading a newer file would ignore the unknown array and restore a
 * logbook silently missing every entry in it. Bumping makes the old build
 * refuse the file and say to update, which is loud and recoverable where the
 * silent version is neither. Newer builds still read older files: the arrays
 * simply default to empty.
 */
export const EXPORT_SCHEMA_VERSION = 4;
/** Dates cross as ISO strings, as they do for sync. */
const isoDate = z.union([z.date(), z.string().datetime({ offset: true })]).pipe(z.coerce.date());
const exportDates = {
    occurredAt: isoDate,
    createdAt: isoDate,
    updatedAt: isoDate,
    deletedAt: isoDate.optional(),
};
export const exportCaseSchema = caseRecordShape.extend(exportDates);
export const exportProcedureSchema = procedureRecordShape
    .extend(exportDates)
    .superRefine(refineProcedure);
export const exportCommunicationSchema = communicationRecordShape.extend(exportDates);
export const exportEdtEntrySchema = edtEntryRecordShape.extend(exportDates);
export const exportAnaestheticCaseSchema = anaestheticCaseRecordShape.extend(exportDates);
export const exportDocumentSchema = z.object({
    schemaVersion: z.number().int().positive(),
    exportedAt: z.string(),
    application: z.string().optional(),
    displayName: z.string().optional(),
    cases: z.array(z.unknown()),
    procedures: z.array(z.unknown()),
    /** Absent in a version 1 file, which is still a file we must read. */
    edtEntries: z.array(z.unknown()).default([]),
    /** Absent before version 3. */
    anaestheticCases: z.array(z.unknown()).default([]),
    /** Absent before version 4. */
    communications: z.array(z.unknown()).default([]),
});
export function buildExport(input) {
    return {
        schemaVersion: EXPORT_SCHEMA_VERSION,
        exportedAt: input.exportedAt.toISOString(),
        application: 'ICULogbook',
        displayName: input.displayName,
        cases: input.cases.map(serialise),
        procedures: input.procedures.map(serialise),
        edtEntries: (input.edtEntries ?? []).map(serialise),
        anaestheticCases: (input.anaestheticCases ?? []).map(serialise),
        communications: (input.communications ?? []).map(serialise),
    };
}
function serialise(entry) {
    const output = {};
    for (const [key, value] of Object.entries(entry)) {
        // `syncedAt` is this device's bookkeeping, not part of the record.
        if (key === 'syncedAt' || value === undefined)
            continue;
        output[key] = value instanceof Date ? value.toISOString() : value;
    }
    return output;
}
/**
 * Reads an export file.
 *
 * Deliberately forgiving about individual entries and strict about the file:
 * a single unreadable procedure is reported and skipped, because refusing the
 * whole restore over one bad row would be the opposite of a safety net.
 */
export function parseExport(input) {
    const document = exportDocumentSchema.safeParse(input);
    if (!document.success) {
        return { ok: false, reason: 'That file is not an ICULogbook export.' };
    }
    if (document.data.schemaVersion > EXPORT_SCHEMA_VERSION) {
        return {
            ok: false,
            reason: `That export was written by a newer version of ICULogbook (format ${String(document.data.schemaVersion)}). Update the app and try again.`,
        };
    }
    const cases = [];
    const procedures = [];
    const edtEntries = [];
    const anaestheticCases = [];
    const communications = [];
    const rejected = [];
    document.data.cases.forEach((row, index) => {
        const parsed = exportCaseSchema.safeParse(row);
        if (parsed.success)
            cases.push(parsed.data);
        else
            rejected.push({ kind: 'case', index, reason: parsed.error.issues[0]?.message ?? 'invalid' });
    });
    document.data.procedures.forEach((row, index) => {
        const parsed = exportProcedureSchema.safeParse(row);
        if (parsed.success)
            procedures.push(parsed.data);
        else {
            rejected.push({
                kind: 'procedure',
                index,
                reason: parsed.error.issues[0]?.message ?? 'invalid',
            });
        }
    });
    document.data.edtEntries.forEach((row, index) => {
        const parsed = exportEdtEntrySchema.safeParse(row);
        if (parsed.success)
            edtEntries.push(parsed.data);
        else
            rejected.push({ kind: 'edt', index, reason: parsed.error.issues[0]?.message ?? 'invalid' });
    });
    document.data.anaestheticCases.forEach((row, index) => {
        const parsed = exportAnaestheticCaseSchema.safeParse(row);
        if (parsed.success)
            anaestheticCases.push(parsed.data);
        else {
            rejected.push({
                kind: 'anaesthetic',
                index,
                reason: parsed.error.issues[0]?.message ?? 'invalid',
            });
        }
    });
    document.data.communications.forEach((row, index) => {
        const parsed = exportCommunicationSchema.safeParse(row);
        if (parsed.success)
            communications.push(parsed.data);
        else {
            rejected.push({
                kind: 'communication',
                index,
                reason: parsed.error.issues[0]?.message ?? 'invalid',
            });
        }
    });
    return {
        ok: true,
        value: {
            displayName: document.data.displayName ?? '',
            exportedAt: document.data.exportedAt,
            cases,
            procedures,
            edtEntries,
            anaestheticCases,
            communications,
            rejected,
        },
    };
}
/* ------------------------------------------------------------ spreadsheet */
/**
 * The spreadsheet export exists to be opened in Excel, so it carries labels
 * rather than keys: "Ward review", not `ward_review`. It is a convenience, not
 * the backup — `restore` reads JSON, and a spreadsheet does not round-trip.
 *
 * There was a CSV export beside it until 2026-08-23, which is why the date and
 * time helpers below are still named `CSV_*`; they are the same two columns
 * either way. See PROGRESS.md for why it went.
 */
const CSV_DATE = (date) => date.toISOString().slice(0, 10);
const CSV_TIME = (date) => `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
function hilloLabels(hillos) {
    return (hillos ?? [])
        .map((number) => `${String(number)} ${hilloByNumber(number)?.shortLabel ?? ''}`.trim())
        .join('; ');
}
/**
 * The rows for each entry type, header first — the single definition of what
 * a spreadsheet export contains, consumed by the `.xlsx` writer in the client.
 *
 * Kept as plain rows rather than folded into that writer: they are the column
 * definition, they are pure and testable here, and a second renderer (there
 * was a CSV one) can read them without the columns being declared twice.
 */
export function casesRows(cases) {
    return [
        [
            'id',
            'date',
            'time',
            'case type',
            'title',
            'age band',
            'capability level',
            'level of care',
            'reasons for referral',
            'taught or supervised',
            'HiLLOs',
            'note',
            'deleted',
        ],
        ...cases.map((record) => [
            record.id,
            CSV_DATE(record.occurredAt),
            CSV_TIME(record.occurredAt),
            labelFor(CASE_TYPE_OPTIONS, record.caseType),
            record.title,
            labelFor(AGE_BAND_OPTIONS, record.ageBand),
            record.capabilityLevel,
            // `=== undefined`, not `??`-on-falsy: level 0 must print as "Level 0"
            // and not as an empty cell.
            record.careLevel === undefined ? '' : careLevelLabel(record.careLevel),
            (record.reasonsForReferral ?? [])
                .map((reason) => labelFor(REFERRAL_REASON_OPTIONS, reason))
                .join('; '),
            (record.teachingGrades ?? [])
                .map((grade) => labelFor(LEARNER_GRADE_OPTIONS, grade))
                .join('; '),
            hilloLabels(record.hillos),
            record.note ?? '',
            record.deletedAt === undefined ? '' : 'yes',
        ]),
    ];
}
export function proceduresRows(procedures) {
    return [
        [
            'id',
            'case id',
            'date',
            'time',
            'procedure',
            'variant',
            'side',
            'ultrasound guided',
            'attempts',
            'successful',
            'complications',
            'indication',
            'age band',
            'capability level',
            'taught or supervised',
            'HiLLOs',
            'note',
            'deleted',
        ],
        ...procedures.map((record) => [
            record.id,
            record.caseId ?? '',
            CSV_DATE(record.occurredAt),
            CSV_TIME(record.occurredAt),
            procedureLabel(record.procedureKey),
            record.variant ?? '',
            record.side === undefined ? '' : labelFor(SIDE_OPTIONS, record.side),
            record.ultrasoundGuided === undefined ? '' : record.ultrasoundGuided ? 'yes' : 'no',
            record.attempts ?? '',
            record.successful === undefined ? '' : record.successful ? 'yes' : 'no',
            record.complications ?? '',
            record.indication ?? '',
            labelFor(AGE_BAND_OPTIONS, record.ageBand),
            record.capabilityLevel,
            (record.teachingGrades ?? [])
                .map((grade) => labelFor(LEARNER_GRADE_OPTIONS, grade))
                .join('; '),
            hilloLabels(record.hillos),
            record.note ?? '',
            record.deletedAt === undefined ? '' : 'yes',
        ]),
    ];
}
export function communicationsRows(records) {
    return [
        [
            'id',
            'date',
            'time',
            'category',
            'with whom',
            'outcome',
            'age band',
            'capability level',
            'taught or supervised',
            'HiLLOs',
            'note',
            'deleted',
        ],
        ...records.map((record) => [
            record.id,
            CSV_DATE(record.occurredAt),
            CSV_TIME(record.occurredAt),
            labelFor(COMMUNICATION_CATEGORY_OPTIONS, record.category),
            (record.withWhom ?? [])
                .map((role) => labelFor(COMMUNICATION_PARTICIPANT_OPTIONS, role))
                .join('; '),
            record.outcome === undefined ? '' : labelFor(COMMUNICATION_OUTCOME_OPTIONS, record.outcome),
            labelFor(AGE_BAND_OPTIONS, record.ageBand),
            record.capabilityLevel,
            (record.teachingGrades ?? [])
                .map((grade) => labelFor(LEARNER_GRADE_OPTIONS, grade))
                .join('; '),
            hilloLabels(record.hillos),
            record.note ?? '',
            record.deletedAt === undefined ? '' : 'yes',
        ]),
    ];
}
export function edtEntriesRows(entries) {
    return [
        ['id', 'date', 'time', 'ends', 'activity', 'title', 'duration', 'HiLLOs', 'note', 'deleted'],
        ...entries.map((record) => [
            record.id,
            CSV_DATE(record.occurredAt),
            CSV_TIME(record.occurredAt),
            // Empty for the ordinary entry that happened at one moment; a date when
            // the work was spread over a period.
            record.endsAt === undefined ? '' : CSV_DATE(record.endsAt),
            labelFor(EDT_ACTIVITY_OPTIONS, record.activity),
            record.title,
            record.durationMinutes === undefined ? '' : formatDuration(record.durationMinutes),
            hilloLabels(record.hillos),
            record.note ?? '',
            record.deletedAt === undefined ? '' : 'yes',
        ]),
    ];
}
export function anaestheticCasesRows(cases) {
    return [
        [
            'id',
            'date',
            'time',
            'setting',
            'title',
            'priority',
            'ASA',
            'age band',
            'capability level',
            'taught or supervised',
            'HiLLOs',
            'note',
            'deleted',
        ],
        ...cases.map((record) => [
            record.id,
            CSV_DATE(record.occurredAt),
            CSV_TIME(record.occurredAt),
            labelFor(ANAESTHETIC_SETTING_OPTIONS, record.setting),
            record.title,
            record.priority === undefined ? '' : labelFor(ANAESTHETIC_PRIORITY_OPTIONS, record.priority),
            record.asa ?? '',
            labelFor(AGE_BAND_OPTIONS, record.ageBand),
            record.capabilityLevel,
            (record.teachingGrades ?? [])
                .map((grade) => labelFor(LEARNER_GRADE_OPTIONS, grade))
                .join('; '),
            hilloLabels(record.hillos),
            record.note ?? '',
            record.deletedAt === undefined ? '' : 'yes',
        ]),
    ];
}
/**
 * Every sheet a spreadsheet export contains, in the order they should appear.
 *
 * Always all five, even where a trainee has logged none of that kind: a
 * workbook whose tabs depend on what happens to be in it is harder to explain
 * than one with a predictable shape, and an empty sheet still carries its
 * header row, which says what would have been there.
 */
export function exportSheets(input) {
    return [
        { name: 'Cases', rows: casesRows(input.cases) },
        { name: 'Procedures', rows: proceduresRows(input.procedures) },
        { name: 'Communications', rows: communicationsRows(input.communications) },
        { name: 'EDT', rows: edtEntriesRows(input.edtEntries) },
        { name: 'Anaesthetics', rows: anaestheticCasesRows(input.anaestheticCases) },
    ];
}
/**
 * What a restore would do, worked out before anything is written.
 *
 * Deduplicated by id, and an id already present is **skipped, never merged**.
 * A restore is for putting back what was lost, not for resolving a conflict:
 * the local copy is the one the trainee has been using, and silently
 * overwriting it with an older export would destroy work rather than save it.
 */
export function planRestore(parsed, existingIds) {
    const cases = parsed.cases.filter((record) => !existingIds.has(record.id));
    const procedures = parsed.procedures.filter((record) => !existingIds.has(record.id));
    const edtEntries = parsed.edtEntries.filter((record) => !existingIds.has(record.id));
    const anaestheticCases = parsed.anaestheticCases.filter((record) => !existingIds.has(record.id));
    const communications = parsed.communications.filter((record) => !existingIds.has(record.id));
    return {
        toImport: { cases, procedures, edtEntries, anaestheticCases, communications },
        skipped: {
            cases: parsed.cases.length - cases.length,
            procedures: parsed.procedures.length - procedures.length,
            edtEntries: parsed.edtEntries.length - edtEntries.length,
            anaestheticCases: parsed.anaestheticCases.length - anaestheticCases.length,
            communications: parsed.communications.length - communications.length,
        },
        rejected: parsed.rejected,
    };
}
/** `ICULogbook_export_2026-08-16.json` */
export function exportFilename(exportedAt, extension) {
    const month = String(exportedAt.getMonth() + 1).padStart(2, '0');
    const day = String(exportedAt.getDate()).padStart(2, '0');
    return `ICULogbook_export_${String(exportedAt.getFullYear())}-${month}-${day}.${extension}`;
}
/**
 * What a report generated for a period is called — the PDF and the Excel
 * workbook alike, so the two files a trainee sends to a supervisor sit
 * together in a folder and obviously belong to each other.
 *
 * Named after the trainee's surname and the period, not the day it was
 * generated: `exportFilename` above is for a backup, where "when did I take
 * this copy" is the question, and this is for a report, where "what does it
 * cover" is.
 */
export function reportFilename(displayName, range, extension) {
    const surname = displayName.trim().split(/\s+/).pop() ?? 'logbook';
    const safe = surname.replace(/[^A-Za-z0-9-]/g, '') || 'logbook';
    const day = (date) => {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const dayOfMonth = String(date.getDate()).padStart(2, '0');
        return `${String(date.getFullYear())}-${month}-${dayOfMonth}`;
    };
    return `ICULogbook_${safe}_${day(range.from)}_to_${day(range.to)}.${extension}`;
}
/** Whether the 90-day export reminder should be shown. Pure, so it is testable. */
export const EXPORT_REMINDER_DAYS = 90;
export const REMINDER_SNOOZE_DAYS = 30;
export function shouldRemindToExport(input) {
    // Nothing to lose yet.
    if (input.entryCount === 0)
        return false;
    const days = (from) => (input.now.getTime() - from.getTime()) / (24 * 60 * 60 * 1000);
    if (input.dismissedAt !== null && days(input.dismissedAt) < REMINDER_SNOOZE_DAYS)
        return false;
    if (input.lastExportAt === null)
        return true;
    return days(input.lastExportAt) >= EXPORT_REMINDER_DAYS;
}
/** Splits a restore's entries back into the three kinds, for the repositories. */
export function partitionEntries(entries) {
    return {
        cases: entries.filter(isCase),
        procedures: entries.filter(isProcedure),
        communications: entries.filter((entry) => !isCase(entry) && !isProcedure(entry)),
    };
}
//# sourceMappingURL=export.js.map